import { Events, SessionProvider } from 'core/infrastructure';
import { CustomerDisplay } from 'pos/services';
import autoPreparePreviousIfNeeded from '../utils/autoPreparePreviousIfNeeded';
import { Message } from 'core/ui-next/modal';
import { FlowAction, Res, TicketDTO, TicketLineDTO, WhenAskForTableCompanions } from 'core/api';
import { useForceUpdate } from 'core/ui-next';
import * as SalesStore from '../SalesStore';
import * as Selection from './ProductSelectionState';
import { useIsOnTop } from 'core/ui-next/modal/ModalHooks';

export const getLastSelectableLine = (ticket: TicketDTO, previousSelectedLine: Nullable<TicketLineDTO>) => {

	if (ticket.IsEmpty)
		return null;

	var updatedSelectedLine = ticket.Lines.find(x => x.Id === previousSelectedLine?.Id);
	if (updatedSelectedLine)
		return updatedSelectedLine;

	const lastLine = ticket.Lines[ticket.Lines.length - 1];
	const lastLineIsInMenu = lastLine.OwnerId !== null && lastLine.OwnerId > 0;

	if (lastLineIsInMenu) {
		const menuHeader = ticket.Lines.filter(x => x.Id === lastLine.OwnerId)[0];
		return menuHeader;
	}

	return lastLine;
};

type Params = {
	productsSelection: Selection.ProductsSelection,
	setProductSelection: (selection: Selection.ProductsSelection) => void,
	applyFlowAction: (action: FlowAction) => Promise<void>,
	setHasCashControlNotification: (hasNotification: boolean) => void,
	setHasTakeOutOrdersNotification: (hasNotification: boolean) => void,
	selectedLine: Nullable<TicketLineDTO>
	selectLine: (line: Nullable<TicketLineDTO>) => void
};


export function useSaleScreenEvents({
	selectedLine,
	productsSelection,
	setProductSelection,
	applyFlowAction,
	setHasCashControlNotification,
	setHasTakeOutOrdersNotification,
	selectLine }: Params) {

	const forceUpdate = useForceUpdate();

	const isOnTop = useIsOnTop();

	Events.useEventOn('pos:sale:splitted-ticket-closed', async invoice => {
		SalesStore.updateLastClosedInvoice(invoice);
	});

	Events.useEventOn('pos:sale:ticket-closed-on-close-day', async invoice => {
		SalesStore.updateLastClosedInvoice(invoice);
	});

	Events.useEventOn('pos:sale:ticket-updated', async ({ oldTicket, newTicket }) => {
		const updated = await Selection.updateOnTicketChanged(productsSelection, oldTicket, newTicket);
		setProductSelection(updated);
	});

	Events.useEventOn('pos:sale:customer-changed', async () => {
		await CustomerDisplay.displayCustomer(SalesStore.getTicket());
		forceUpdate();
	});

	Events.useEventOn('pos:sale:ticket-prepared', async () => {
		await applyFlowAction(SessionProvider.getCurrent().AfterPrepareTicketAction);
	});

	Events.useEventOn('pos:sale:ticket-payments-changed', async sale => {
		await CustomerDisplay.displaySale(sale);
	});

	Events.useEventOn('pos:cash-control:show-notification', async () => {
		setHasCashControlNotification(true);
	});

	Events.useEventOn('pos:cash-control:hide-notification', async () => {
		setHasCashControlNotification(false);
	});

	Events.useEventOn('pos:takeout-order:new-orders-count', async ({ count }) => {
		setHasTakeOutOrdersNotification(count > 0);
	});

	Events.useEventOn('pos:sale:operation-failed', async error => {
		await Message.show(error.msg);
	});

	Events.useEventOn('pos:sale:preparation-order-changed', async () => {
		forceUpdate();
	});

	Events.useEventOn('pos:seat-number:changed', async () => {
		forceUpdate();
	});

	Events.useEventOn('session:timeout', async () => {
		if (isOnTop) {
			await applyFlowAction(FlowAction.ChangeUser);
		}
	});

	Events.useEventOn('pos:sale:ticket-closed', async lastSale => {
		forceUpdate();

		await CustomerDisplay.displaySale(lastSale);
		const action = SessionProvider.getAfterCloseTicketAction(SalesStore.getTicket().Location);
		await applyFlowAction(action);
	});

	Events.useEventOn('pos:split-ticket:completed', async ({ operation }) => {
		await SalesStore.updateTicket();
		if (operation === 'close') {
			const action = SessionProvider.getAfterCloseTicketAction(SalesStore.getTicket().Location);
			await applyFlowAction(action);
		}
	});

	Events.useEventOn('pos:sale:ticket-printed', async flowMode => {
		if (flowMode !== 'IGNORE_TICKET_PRINTED_FLOW') {
			await applyFlowAction(SessionProvider.getCurrent().AfterPrintTicketAction);
		}
	});

	Events.useEventOn('pos:sale:ticket-cancelled', async cancelledTicket => {
		if (cancelledTicket.Location.AskForTableCompanions === WhenAskForTableCompanions.OnOpenDocument) {
			await applyFlowAction(FlowAction.ChangeLocation);
		}
	});

	Events.useEventOn('pos:sale:new-ticket-loaded', async () => {
		await CustomerDisplay.displayTicketTotal(SalesStore.getTicket());
		await autoPreparePreviousIfNeeded(SalesStore.getPreviousTicket());
	});

	Events.useEventOn('pos:sale:low-stock-warning', async warnings => {
		const message = [
			Res.Msg.MSG_PRODUCTS_UNDER_MIN_STOCK(),
			'\r\n',
			...warnings.map(w =>
				Res.Msg.MSG_PRODUCT_STOCK_STATUS(
					w.ProductName,
					w.MinStock,
					w.CurrentStock - w.RequestedQuantity
				)
			)
		].join('');
		await Message.show(message);
	});

	Events.useEventOn('pos:sale:ticket-updated', async ({ newTicket, oldTicket }) => {

		// Si la línea que debería seleccionar sin contar la línea seleccionada actual, es la misma, quiere decir que el ticket
		// no ha cambiado de líneas, simplemente se ha actualizado, pero no se ha añadido ninguna nueva.
		// En ese caso lo que tengo que hacer es mantener la línea que ya estaba seleccionada o en su defecto la nueva línea seleccionada.
		const oldSelectedLine = oldTicket ? getLastSelectableLine(oldTicket, null) : null;
		const newSelectedLine = getLastSelectableLine(newTicket, null);

		const lineToSelect = oldSelectedLine?.Id === newSelectedLine?.Id
			? getLastSelectableLine(newTicket, selectedLine)
			: newSelectedLine;

		selectLine(lineToSelect);
	});

}